import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'ui/atoms/translate';
import { AdminApi, AdminCampaignsListOrderingEnum, AdminCampaignsValuesRetrieveFieldEnum, Campaign } from 'api';
import Currency from 'ui/atoms/currency';
import { ProductCampaignsStatus } from 'apps/issuer/pages/product-details/campaigns/shared/product-campaign-status';
import WithDataRecord from 'src/hoc/WithDataRecord';
import { PaginationProps } from 'ui/molecules/pagination';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';

export interface ProductCampaignsTableProps {
  productId: string;
  title?: ReactNode;
  loading?: boolean;
  paginationProps?: PaginationProps;
  goToDetails: (campaignId: string) => void;
  defaultLimit?: number;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  skipFilterUrlSync?: boolean;
  setFilterPopupHeight?: (height: number) => void;
}

const ProductCampaignsTable: FunctionComponent<ProductCampaignsTableProps> = WithDataRecord<AdminApi, Campaign>(
  AdminApi,
)(
  ({ data, ordering, onOrderBy, title, loading, goToDetails, paginationProps, setFilterPopupHeight, productId }) => {
    const { makeAuthenticatedApi } = useApiCall();
    const translate = useTranslateWithStringArgs();
    const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

    const campaignStatusFilterOptions = useMemo(() => {
      ['true', 'false'].map((active) => ({
        id: active,
        name: translate(`issuerProductDetails.campaigns.status.${active === 'true' ? 'active' : 'deactivated'}`),
      }));
    }, [translate]);

    return (
      <>
        <DataRecordTable
          data={data}
          ordering={ordering}
          onOrderBy={onOrderBy}
          title={title}
          loading={loading}
          paginationProps={paginationProps}
          onGoToDetails={goToDetails}
          setFilterPopupHeight={setFilterPopupHeight}
          cells={[
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.reference`} />,
              render: (campaign) => campaign.name || '-',
              orderingFieldName: AdminCampaignsListOrderingEnum.NAME_ASC,
              type: CellTypeEnum.text,
              filterName: 'name',
              searchFieldName: AdminCampaignsValuesRetrieveFieldEnum.NAME,
              getFilterData: (params: any) =>
                adminApi.adminCampaignsValuesRetrieve({ ...params, productId: [productId] }),
            },
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.distributionPlatform`} />,
              render: (campaign) => campaign.distributionPlatformDetails.displayName || '-',
              orderingFieldName: AdminCampaignsListOrderingEnum.DISTRIBUTION_PLATFORM_ASC,
              type: CellTypeEnum.text,
              filterName: 'distributionPlatformDisplayName',
              searchFieldName: AdminCampaignsValuesRetrieveFieldEnum.DISTRIBUTION_PLATFORM,
              getFilterData: (params: any) =>
                adminApi.adminCampaignsValuesRetrieve({ ...params, productId: [productId] }),
            },
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.allocation`} />,
              alignContent: 'right',
              render: (campaign) => campaign.allocation,
              orderingFieldName: AdminCampaignsListOrderingEnum.ALLOCATION_AVAILABLE_ASC,
              type: CellTypeEnum.number,
              filterName: 'allocationAvailable',
            },
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.signed`} />,
              alignContent: 'right',
              render: (campaign) => `${campaign.signedPercentage.toFixed()}%`,
              orderingFieldName: AdminCampaignsListOrderingEnum.SIGNED_PERCENTAGE_ASC,
              type: CellTypeEnum.percentage,
              filterName: 'signedPercentage',
            },
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.pricePerUnit`} />,
              alignContent: 'right',
              render: (campaign) => <Currency decimals={'*'}>{campaign.pricePerToken}</Currency>,
              orderingFieldName: AdminCampaignsListOrderingEnum.PRICE_PER_UNIT_ASC,
              type: CellTypeEnum.currency,
              filterName: 'pricePerUnit',
            },
            {
              title: <Translate name={`issuerProductDetails.campaigns.table.status`} />,
              render: (campaign) => <ProductCampaignsStatus active={campaign.active} />,
              width: 138,
              orderingFieldName: AdminCampaignsListOrderingEnum.ACTIVE_ASC,
              type: CellTypeEnum.text,
              filterName: 'active',
              getFilterData: () => {
                return campaignStatusFilterOptions;
              },
            },
          ]}
        />
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    return api.adminCampaignsList({
      offset,
      limit,
      ordering: ordering as AdminCampaignsListOrderingEnum,
      ...props,
      productId: [props.productId],
    });
  },
);

export default ProductCampaignsTable;
